import { useState } from "react";
import { Lock, Phone, Mail, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "mentor", label: "Mentor" },
  { key: "food", label: "Food" },
  { key: "career", label: "Career" },
  { key: "family", label: "Family" },
  { key: "mental_health", label: "Mental Health" },
  { key: "prayer", label: "Prayer" },
  { key: "other", label: "Other" },
];

export default function GetHelp() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    category: "mentor",
    urgency: "normal",
    message: "",
    preferred_contact: "email",
    confidential: true,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/help-requests", {
        ...form,
        email: form.email || null,
        phone: form.phone || null,
      });
      toast.success("Your request was received. A KEYZ team member will reach out within 48 hours.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Could not submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="get-help-page">
      <section className="section">
        <div className="container-editorial grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Get help</div>
            <h1 className="mt-4 font-serif text-5xl leading-[1.05] text-ink md:text-6xl">
              You don't have to <em className="not-italic text-clay">figure this out</em> alone.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink-muted">
              This form goes to a small, trained team at KEYZ. Whatever you're navigating, we'll respond within
              48 hours with real support — no judgment, no strings.
            </p>

            <div className="mt-10 space-y-4">
              <div className="flex items-start gap-4 rounded-2xl border border-clay/10 bg-white p-5">
                <Lock className="mt-1 h-5 w-5 text-clay" />
                <div>
                  <div className="font-serif text-lg text-ink">Confidential by default</div>
                  <p className="text-sm text-ink-muted">Only our care team sees this. Nothing shared without your consent.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-clay/10 bg-white p-5">
                <ShieldCheck className="mt-1 h-5 w-5 text-sage" />
                <div>
                  <div className="font-serif text-lg text-ink">Not an emergency service</div>
                  <p className="text-sm text-ink-muted">If you or someone you love is in immediate danger, call <a href="tel:988" className="text-clay underline">988</a> or 911.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-clay/10 bg-white p-5">
                <Mail className="mt-1 h-5 w-5 text-clay" />
                <div>
                  <div className="font-serif text-lg text-ink">Prefer email?</div>
                  <p className="text-sm text-ink-muted">
                    <a href="mailto:keyzfoundationinc@gmail.com" className="text-clay underline">keyzfoundationinc@gmail.com</a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="rounded-3xl border border-sage/30 bg-sage p-10 text-oat">
                <div className="font-serif text-3xl">Thank you.</div>
                <p className="mt-3">Your request has been received. A KEYZ team member will reach out within 48 hours.</p>
                <button onClick={() => setSubmitted(false)} data-testid="submit-another" className="mt-8 rounded-full bg-oat px-5 py-2.5 text-sm font-bold text-sage">
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-clay/15 bg-white p-8 md:p-10">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="field-label">Full name *</label>
                    <input required data-testid="help-full-name" className="field-input" value={form.full_name} onChange={set("full_name")} />
                  </div>
                  <div>
                    <label className="field-label">Email</label>
                    <input type="email" data-testid="help-email" className="field-input" value={form.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label className="field-label">Phone</label>
                    <input data-testid="help-phone" className="field-input" value={form.phone} onChange={set("phone")} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="field-label">What kind of support are you looking for? *</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {CATEGORIES.map((c) => (
                        <button
                          type="button"
                          key={c.key}
                          data-testid={`help-cat-${c.key}`}
                          onClick={() => setForm((f) => ({ ...f, category: c.key }))}
                          className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                            form.category === c.key
                              ? "border-clay bg-clay text-oat"
                              : "border-clay/25 text-ink hover:border-clay hover:text-clay"
                          }`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="field-label">Urgency</label>
                    <select data-testid="help-urgency" className="field-input" value={form.urgency} onChange={set("urgency")}>
                      <option value="low">Low — whenever you can</option>
                      <option value="normal">Normal — within the week</option>
                      <option value="high">High — this week matters</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Preferred contact</label>
                    <select data-testid="help-contact-pref" className="field-input" value={form.preferred_contact} onChange={set("preferred_contact")}>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="text">Text message</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="field-label">Tell us what's going on *</label>
                    <textarea required rows={5} data-testid="help-message" className="field-input" value={form.message} onChange={set("message")} placeholder="You can share as much or as little as you're comfortable with." />
                  </div>

                  <label className="sm:col-span-2 mt-2 flex items-start gap-3 text-sm text-ink-muted">
                    <input type="checkbox" data-testid="help-confidential" checked={form.confidential} onChange={set("confidential")} className="mt-1 h-4 w-4 accent-clay" />
                    <span>Keep this confidential — only share with the KEYZ care team.</span>
                  </label>

                  <div className="sm:col-span-2 mt-2 flex justify-end">
                    <button type="submit" disabled={loading} data-testid="help-submit" className="btn-primary">
                      {loading ? "Sending…" : "Request Support"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
